<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";
import type { VForm } from "vuetify/components";
import DatePicker from "@/components/pickers/datePicker/DatePicker.vue";
import TimePicker from "@/components/pickers/TimePicker.vue";
import OmniSelect from "@/components/selects/OmniSelect.vue";
import TimezoneSelect from "@/components/selects/TimezoneSelect.vue";
import BolNumberHandler from "@/views/trips/components/BolNumberHandler.vue";
import FileUploadingGrid from "@/views/trips/components/FileUploadingGrid.vue";
import { API } from "@/api/api";
import { useBaseView, useDateTime } from "@/composables";
import { useError } from "@/composables/useError";
import { useValidation } from "@/composables/useValidation";
import { OrderFreightType, OrderStopType } from "@/constants/order";
import { RouteStopStatus } from "@/constants/trip";
import { toInt } from "@/utils/number";
import BolValidator from "@/validations/bol.validator";
import type { Bol, RouteStopDto, TravelOrderOrderInfo } from "@/api/trips/dto/trip.dto";
import type { StopInfoFormProps } from "@/types/props";
import type { PickupInfoFormData, StopInfoFile, StopInfoFreight, TripRouteFreight, TripRouteStop } from "@/types/trip";

const { t } = useI18n();

const loading = defineModel<boolean>("loading", { default: false });

const props = withDefaults(defineProps<StopInfoFormProps>(), {
  preload: false,
  readonly: false,
});

const emits = defineEmits<{
  (e: "save"): void;
}>();

const api = new API();

const { dateToUTC, timezonesShortList } = useDateTime();
const { getPreviewPath, uniqueArray } = useBaseView();
const { requiredValidator, digitsValidator } = useValidation();
const { showAPIError, scrollToError } = useError();

const formRef = useTemplateRef<VForm | null>("form");
const loadData = ref<PickupInfoFormData>({
  bols: [],
  bolNotAvailable: false,
  actualFreights: [],
  files: [],
  date: "",
  time: "",
  timezone: "",
});

const isBolsValidated = ref<boolean>(false);
const isBolEditing = ref<boolean>(false);

const isLoaded = computed(() => {
  return loading.value;
});

const isStopVerified = computed<boolean>(() =>
  [RouteStopStatus.waitingForGoodToGo, RouteStopStatus.completed].includes(props.stop.status),
);

const isBolReadonly = computed<boolean>(() => props.readonly && !isBolEditing.value);

const routeStops = computed<(TripRouteStop | RouteStopDto)[]>(() => props.route.routeStops);

const relatedOrder = computed<TravelOrderOrderInfo | null>(() => {
  return (
    props.route.travelOrders.find((to) => {
      if (!to.id) {
        return null;
      }

      return props.stop.travelOrderIds?.includes(to.id);
    })?.order || null
  );
});

const reusedBols = computed<Bol[]>(() =>
  (props.route.bols ?? []).filter((bol) => bol.orderId === relatedOrder.value?.id),
);

const freights = computed<TripRouteFreight[]>(
  () =>
    uniqueArray(
      props.route.freights.filter((f) => props.stop.freights.includes(f.id)),
      ["id"],
    ) as TripRouteFreight[],
);

const stopFreights = computed<TripRouteFreight[]>(() =>
  props.stop.freights.map((sf) => freights.value.find((f) => f.id === sf)).filter((f) => f !== undefined),
);

const stopBols = computed<Bol[]>(() => {
  const bolIds = stopFreights.value.flatMap((f) => f.bolIds ?? []);

  return (props.route.bols ?? []).filter((bol) => bolIds.includes(bol.id));
});

const actualPalletsQuantity = computed(() =>
  loadData.value.actualFreights.reduce(
    (sum, f) => (f.type === OrderFreightType.pallets ? sum + toInt(f.quantity) : sum),
    0,
  ),
);

const palletsQuantity = computed(() =>
  freights.value.reduce((sum, f) => (f.type === OrderFreightType.pallets ? sum + toInt(f.plannedQuantity) : sum), 0),
);

const actualPiecesQuantity = computed(() =>
  loadData.value.actualFreights.reduce(
    (sum, f) => (f.type === OrderFreightType.pieces ? sum + toInt(f.quantity) : sum),
    0,
  ),
);

const piecesQuantity = computed(() =>
  freights.value.reduce((sum, f) => (f.type === OrderFreightType.pieces ? sum + toInt(f.plannedQuantity) : sum), 0),
);

const actualFreightsWeight = computed(() => loadData.value.actualFreights.reduce((sum, f) => sum + toInt(f.weight), 0));

const freightsWeight = computed(() => freights.value.reduce((sum, f) => sum + toInt(f.plannedWeight), 0));

const actualPalletsQuantityClass = computed(() =>
  getComparisonClass(actualPalletsQuantity.value, palletsQuantity.value),
);

const actualPiecesQuantityClass = computed(() => getComparisonClass(actualPiecesQuantity.value, piecesQuantity.value));

const actualFreightsWeightClass = computed(() => getComparisonClass(actualFreightsWeight.value, freightsWeight.value));

const bolFreightOptions = computed(() => {
  return loadData.value.bols
    .filter((bol) => {
      return bol !== null;
    })
    .map((bol) => {
      return {
        name: bol.number,
        id: bol.id,
      };
    });
});

const bolFileOptions = computed(() => {
  return loadData.value.bols
    .filter((bol) => {
      return bol !== null;
    })
    .map((bol) => {
      return {
        name: bol.number,
        id: bol.number,
      };
    });
});

const bolErrors = computed<(string | null)[]>(() => {
  if (!isBolsValidated.value) {
    return [];
  }

  return new BolValidator(loadData.value.bols, loadData.value.actualFreights).validate();
});

const hasBols = computed(() => {
  return loadData.value.bols.filter(Boolean).length > 0;
});

const singleBol = computed<Bol | null>(() => {
  return loadData.value.bols[0];
});

const newFiles = computed<StopInfoFile[]>(() => loadData.value.files.filter((file) => Boolean(file.file)));

const retaggedFiles = computed<StopInfoFile[]>(() => {
  return loadData.value.files.filter((file) => !file.file && file.bol !== getTaggedBol(file));
});

const freightsQuantity = computed(() => {
  const result = [];
  if (palletsQuantity.value > 0) {
    result.push(palletsQuantity.value + " plt");
  }
  if (piecesQuantity.value > 0) {
    result.push(piecesQuantity.value + " pcs");
  }
  return result.join(", ");
});

function applyBol(): void {
  isBolEditing.value = false;
}

function toggleBolEditing(): void {
  if (!isBolEditing.value) {
    isBolEditing.value = true;

    return;
  }

  applyBol();
}

function getTaggedBol(file: StopInfoFile): string | undefined {
  return file.tags?.find((tag) => tag.startsWith("bol:"))?.split(":")[1];
}

function getFileTags(file: StopInfoFile): string[] {
  const tags = (file.tags ?? []).filter((tag) => tag !== "bol" && !tag.startsWith("bol:"));

  return [...tags, file.bol ? "bol:" + file.bol : "bol"];
}

function getComparisonClass(actual: number, planned: number): string {
  if (actual > planned) {
    return "text-error";
  } else if (actual < planned) {
    return "text-warning";
  } else {
    return "text-success";
  }
}

function getFreightDeliveryPoint(freight: StopInfoFreight): string | null {
  const facility = routeStops.value.find(
    (o) => o.type === OrderStopType.delivery && o.freights.includes(freight.id),
  )?.facility;
  return facility ? facility.addressLine + ", " + facility.address : null;
}

function getFreightData(freight: StopInfoFreight): string {
  return freight.plannedQuantity + " " + getFreightType(freight) + ", " + freight.plannedWeight + " lbs";
}

function getFreightType(freight: StopInfoFreight): string {
  if (freight.type === OrderFreightType.pallets) return "plt";
  return "pcs";
}

function removeMissingBols(): void {
  const bolIds = bolFreightOptions.value.map((option) => option.id);

  loadData.value.actualFreights = loadData.value.actualFreights.map((freight) => {
    return {
      ...freight,
      bolIds: freight.bolIds?.filter((bolId) => bolIds.includes(bolId)),
    };
  });
}

function setBolsAutomatically(): void {
  if (loadData.value.bols.length > 1 && hasBols.value) {
    return;
  }

  const bolId = singleBol.value?.id;
  const bolNumber = singleBol.value?.number;

  if (loadData.value.files.length) {
    loadData.value.files = loadData.value.files.map((file) => {
      return {
        ...file,
        bol: bolNumber,
      };
    });
  }

  loadData.value.actualFreights = loadData.value.actualFreights.map((freight) => {
    return {
      ...freight,
      bolIds: bolId ? [bolId] : [],
    };
  });
}

async function init(): Promise<void> {
  let freightNumber = 1;
  loadData.value.actualFreights = stopFreights.value.map((f) => ({
    ...f,
    weight: f.weight || f.plannedWeight,
    quantity: f.quantity || f.plannedQuantity,
    number: freightNumber++,
  }));

  loadData.value.bols = stopBols.value.length ? [...stopBols.value] : [null];

  if (!props.preload) {
    loadData.value.timezone = props.stop.facility?.timezone ?? "";
    const date = dayjs().tz(loadData.value.timezone);
    loadData.value.date = date.format("YYYY-MM-DD");
    loadData.value.time = date.format("HH:mm");

    return;
  }

  loadData.value.timezone = props.stop.timezone ?? "";
  const date = dayjs(props.stop.loadUnloadTime).tz(loadData.value.timezone);
  loadData.value.date = date.format("YYYY-MM-DD");
  loadData.value.time = date.format("HH:mm");

  try {
    const response = await api.trips.getTripFiles(props.tripId);

    if (response.success && response.data) {
      loadData.value.files = response.data
        .filter((f) => f.tags?.includes("route_stop:" + props.stop.id))
        .map((f) => ({
          ...f,
          previewPath: getPreviewPath(f),
          bol: getTaggedBol(f),
        }));
    }
  } catch (error) {
    console.error(error);
  }
}

async function uploadFiles(): Promise<void> {
  const uploadPromises = newFiles.value.map(async (uploadingFile) => {
    uploadingFile.loading = true;

    try {
      const response = await api.trips.uploadRouteStopFile(
        props.tripId,
        String(props.stop.id),
        "bol",
        uploadingFile.file!,
        uploadingFile.bol,
      );

      if (response.success && response.data) {
        Object.assign(uploadingFile, {
          ...response.data,
          previewPath: getPreviewPath(response.data),
        });
      } else {
        uploadingFile.error = true;
      }
    } catch (error) {
      console.error(error);
      uploadingFile.error = true;
    } finally {
      uploadingFile.loading = false;
    }
  });

  await Promise.all(uploadPromises);
}

async function retagFiles(): Promise<void> {
  const retagPromises = retaggedFiles.value.map(async (retaggingFile) => {
    retaggingFile.loading = true;

    try {
      const response = await api.files.updateFileTags(retaggingFile.id, getFileTags(retaggingFile));

      if (response.success && response.data) {
        Object.assign(retaggingFile, {
          ...response.data,
          previewPath: getPreviewPath(response.data),
        });
      } else {
        retaggingFile.error = true;
      }
    } catch (error) {
      console.error(error);
      retaggingFile.error = true;
    } finally {
      retaggingFile.loading = false;
    }
  });

  await Promise.all(retagPromises);
}

async function save(): Promise<void> {
  isBolsValidated.value = true;

  const result = await formRef.value?.validate();

  if (!result?.valid || bolErrors.value.some(Boolean)) {
    await nextTick();
    scrollToError(formRef.value?.$el);

    return;
  }

  loading.value = true;

  if (newFiles.value.length) {
    await uploadFiles();
  }

  if (retaggedFiles.value.length) {
    await retagFiles();
  }

  const stopId = String(props.stop.id);
  const verifyPayload = {
    bols: hasBols.value ? loadData.value.bols : [],
    freights: loadData.value.actualFreights.map((f) => {
      return {
        id: f.id,
        bolIds: f.bolIds,
      };
    }),
  };

  const loadedPayload = {
    bols: hasBols.value ? loadData.value.bols : [],
    freights: loadData.value.actualFreights,
  };

  try {
    const response = isStopVerified.value
      ? await api.trips.verifyRouteStop(props.tripId, stopId, verifyPayload)
      : await api.trips.loadRouteStop(props.tripId, stopId, {
          ...loadedPayload,
          loadTime: dateToUTC(loadData.value.date + " " + loadData.value.time, loadData.value.timezone),
        });

    if (response.success) {
      emits("save");
    } else {
      showAPIError(
        response,
        isStopVerified.value ? t("trip.verify-update-failed") : t("trip.load-verification-failed"),
      );
    }
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
}

watch(
  () => loadData.value.bols,
  () => {
    removeMissingBols();
    setBolsAutomatically();
  },
  { deep: true },
);

onMounted(() => {
  init();
});

defineExpose({ save });
</script>

<template>
  <v-form ref="form">
    <div class="mb-6">
      <div class="d-flex align-center justify-space-between">
        <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">BOL info</div>

        <v-btn
          v-if="Boolean(stopBols.length)"
          :prepend-icon="isBolEditing ? 'mdi-check' : 'mdi-pencil'"
          :variant="isBolEditing ? 'flat' : 'outlined'"
          color="primary"
          data-qa="edit-bol-button"
          density="comfortable"
          @click="toggleBolEditing"
        >
          {{ isBolEditing ? t("general.apply") : t("trip.edit-bol") }}
        </v-btn>
      </div>
      <bol-number-handler
        v-model="loadData.bols"
        v-model:not-available="loadData.bolNotAvailable"
        :errors="bolErrors"
        :order="relatedOrder"
        :readonly="isBolReadonly"
        :reused-bols="reusedBols"
        :route-stop="stop"
        :route-stops="routeStops"
      />
    </div>
    <div class="mb-6">
      <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">
        Files<span v-if="!loadData.bolNotAvailable" class="text-error">*</span>
      </div>

      <file-uploading-grid
        v-model="loadData.files"
        :disabled="readonly"
        :required="hasBols"
        @load="setBolsAutomatically"
      >
        <template #footer="{ file }">
          <omni-select
            v-if="file"
            v-model="file.bol"
            :disabled="isBolReadonly || loadData.bolNotAvailable"
            :items="bolFileOptions"
            :required="!loadData.bolNotAvailable"
            :rules="loadData.bolNotAvailable ? [] : [requiredValidator]"
            class="mt-5"
            data-qa="bol-select-file-zone"
            label="BOL"
          ></omni-select>
        </template>
      </file-uploading-grid>
    </div>
    <div class="mb-6">
      <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">Freights at stop:</div>
      <div v-for="actualFreight in loadData.actualFreights" :key="actualFreight.id">
        <div class="bg-grey-lighten-4 pa-4">
          <div class="text-grey-darken-3 font-weight-500 mb-2">Freight#{{ actualFreight.number }}:</div>
          <div class="d-flex align-center justify-space-between">
            <div>
              <span class="text-grey-darken-2 font-weight-500">Planned: </span>
              <span class="text-grey-darken-3 font-weight-500" data-qa="freight-block-planned"
                >#{{ actualFreight.number }}: {{ getFreightData(actualFreight) }}</span
              >
            </div>
            <div class="d-flex align-center text-grey-darken-1">
              <v-icon class="mr-2" color="error" icon="mdi-map-marker-radius-outline" size="18"></v-icon>
              #{{ actualFreight.number }}: {{ getFreightDeliveryPoint(actualFreight) }}
            </div>
          </div>
        </div>
        <div class="bg-grey-lighten-5 pa-4">
          <div class="text-grey-darken-3 font-weight-500 mb-3">Actual:</div>
          <v-row>
            <v-col>
              <v-text-field
                v-model="actualFreight.quantity"
                :disabled="readonly"
                :rules="[requiredValidator, digitsValidator]"
                autocomplete="off"
                class="required bg-white"
                color="primary"
                data-qa="quantity-field"
                density="compact"
                hide-details="auto"
                label="Quantity"
                variant="outlined"
              />
            </v-col>
            <v-col>
              <v-text-field
                v-model="actualFreight.weight"
                :disabled="readonly"
                :rules="[requiredValidator, digitsValidator]"
                autocomplete="off"
                class="required bg-white"
                color="primary"
                data-qa="total-weight-field"
                density="compact"
                hide-details="auto"
                label="Total weight"
                variant="outlined"
              />
            </v-col>
            <v-col>
              <omni-select
                v-model="actualFreight.bolIds"
                :disabled="isBolReadonly || loadData.bolNotAvailable"
                :items="bolFreightOptions"
                :required="!loadData.bolNotAvailable"
                :rules="loadData.bolNotAvailable ? [] : [requiredValidator]"
                class="bg-white"
                data-qa="actual-freight-bol-select"
                label="BOL"
                multiple
              ></omni-select>
            </v-col>
            <v-col>
              <v-text-field
                v-model="actualFreight.sealNumber"
                :disabled="readonly"
                autocomplete="off"
                class="bg-white"
                color="primary"
                data-qa="seal-number-field"
                density="compact"
                hide-details="auto"
                label="Seal number"
                variant="outlined"
              />
            </v-col>
          </v-row>
        </div>
      </div>
      <div>
        <div class="text-grey-darken-3 font-weight-500 mt-4 mb-2 text-right" data-qa="total-planned-freigth">
          Planned total: {{ freightsQuantity }}, {{ freightsWeight }} lbs
        </div>
        <div class="font-weight-500 text-grey-darken-3 text-right" data-qa="total-freight">
          Total:
          <span :class="actualPalletsQuantityClass">{{ actualPalletsQuantity }}</span> plt,
          <span :class="actualPiecesQuantityClass">{{ actualPiecesQuantity }}</span> pcs,
          <span :class="actualFreightsWeightClass">{{ actualFreightsWeight }}</span> lbs
        </div>
      </div>
    </div>
    <div>
      <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">Time</div>
      <v-row>
        <v-col cols="3">
          <timezone-select
            v-model="loadData.timezone"
            :disabled="readonly"
            :items="timezonesShortList"
            :rules="[requiredValidator]"
            required
          />
        </v-col>
        <v-col cols="6">
          <date-picker v-model="loadData.date" :disabled="readonly" label="Date" required></date-picker>
        </v-col>
        <v-col cols="3">
          <time-picker v-model="loadData.time" :disabled="readonly" label="Time" required></time-picker>
        </v-col>
      </v-row>
    </div>
  </v-form>
</template>

<style lang="scss" scoped></style>
